import React, { useState, useEffect } from 'react';
import { Stack, Text, PrimaryButton } from '@fluentui/react';
import { getConsumptionAPI } from '../../utils/httpUtils';
import { useDispatch } from 'react-redux';
import { SETHasServiceRequestTypeLogData, SETIsCustomeLog, SETLogEventData } from '../../../core/store';
import { sanitizeHtml } from '../../utils/htmlSanitizer';
import { buildCssFromJson } from '../../utils/cssFromJson';
 
export interface FeedbackData {
    id: number;
    tabName: string;
    content: {
        [key: string]: any;
    };
    iconUrl: string;
    linkDetails: Array<{
        linkText: string;
        linkUrl: string;
    }>;
    createdOn: string;
}
 
interface FeedbackTabComponentProps {
    context?: any;
}

type FeedbackSection = 'headerSection' | 'bodySection' | 'footerSection';

// Deterministic, name-based lookup for the HTML sections (falls back to the
// legacy positional index for older DB rows). Only string sections are returned;
// the CSS now lives under the object key `commonStyles` and is handled separately.
const getFeedbackSection = (
    content: FeedbackData['content'],
    section: FeedbackSection,
    legacyIndex: number
): string | null => {
    const named = Object.entries(content).find(
        ([key, value]) => key.toLowerCase() === section.toLowerCase() && typeof value === 'string'
    );
    if (named) {
        return named[1] as string;
    }

    const legacyValue = Object.values(content)[legacyIndex];
    return typeof legacyValue === 'string' ? legacyValue : null;
};

// Resolve the structured CSS map (new `commonStyles` object) or, for backward
// compatibility, ignore a legacy `commonSection` <style> string (which the
// sanitizer strips) and return null so the component renders no stale styles.
const getCommonStyles = (content: FeedbackData['content']): Record<string, Record<string, string>> | null => {
    const named = Object.entries(content).find(
        ([key, value]) =>
            key.toLowerCase() === 'commonstyles' &&
            value && typeof value === 'object' && !Array.isArray(value)
    );
    return named ? (named[1] as Record<string, Record<string, string>>) : null;
};
 
const FeedbackTabComponent: React.FC<FeedbackTabComponentProps> = ({ context }) => {
 
    let blobToken = sessionStorage.getItem("SasTokenKey") || "";
 
    useEffect(() => {
        fetchFeedbackData();
        preloadImages();
    }, [context, blobToken]);
 
    const [feedbackData, setFeedbackData] = useState<FeedbackData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const reduxDispatch = useDispatch();
 
    // Function to preload Person Feedback image
    const preloadImages = () => {
        if (blobToken) {
            const feedbackImg = new Image();
            feedbackImg.src = `${process.env.REACT_APP_BLOBSASSTART}Person Feedback.svg${blobToken}`;
        }
    };
    const replaceBlobUrl = (html: string | null | undefined): string => {
        if (!html) return "";
        const suffixBlobToken = blobToken || "";
        const base = process.env.REACT_APP_BLOBSASSTART || "";
        // Replace all literal occurrences of {BLOB_URL}
        html = html.replace(/{BLOB_URL}/g, base);
        // Replace all literal occurrences of {BLOB_TOKEN}
        return html.replace(/{BLOB_TOKEN}/g, suffixBlobToken);
    };
 
    // API call to fetch feedback data
    const fetchFeedbackData = async () => {
        try {
            setLoading(true);
 
            if (context?.authClient) {
                const response = await getConsumptionAPI('/api/FeedbackPage', context.authClient);
 
                if (response?.status === 200 && response?.data) {
                    setFeedbackData(response.data);
                } else {
                    throw new Error('Invalid response from server');
                }
            } else {
                throw new Error('Authentication context not available');
            }
        } catch (err) {
            console.error('Error fetching feedback data:', err);
            setError('Oops! Something went wrong while loading the feedback content. Please try again later.');
            setFeedbackData(null);
        } finally {
            setLoading(false);
        }
    };
 
    if (loading) {
        return (
            <Stack
                verticalAlign="center"
                horizontalAlign="center"
                styles={{ root: { minHeight: "400px" } }}
            >
                <Text styles={{
                    root: {
                        fontSize: '24px',
                        fontWeight: '600',
                        color: '#000'
                    }
                }}>
                    Please wait, loading feedback content...
                </Text>
            </Stack>
        );
    }
 
    if (error || !feedbackData) {
        return (
            <Stack
                verticalAlign="center"
                horizontalAlign="center"
                styles={{ root: { minHeight: "400px" } }}
            >
                <Text styles={{
                    root: {
                        fontSize: '24px',
                        fontWeight: '600',
                        color: '#000'
                    }
                }}>
                    {error || "Failed to load feedback content"}
                </Text>
            </Stack>
        );
    }
 
    const handleButtonClick = (linkUrl: string, linkText: string) => {
        const name = linkText + '-' + linkUrl;
        //Start Event Logging 
        reduxDispatch(SETHasServiceRequestTypeLogData(true));
        reduxDispatch(SETLogEventData(
            {
                elementName: `${name || 'Feedback'} Button`,
                elementId: "",
                action: "click",
                message: `User clicked on ${linkText || 'Feedback'} button to open external feedback form - URL: ${linkUrl}`,
                profileSetting: `v2`,
            }
        ));
        reduxDispatch(SETIsCustomeLog(true));
        //End Event Logging
 
        openIframe(linkUrl);
    };
 
    const openIframe = (iframeSrc: string) => {
        try {
            const topOffset = Math.floor(window.screen.height * 0.25); // 25% from top for header
            const footerOffset = Math.floor(window.screen.height * 0.08); // 8% from bottom for footer
            const adjustedHeight = window.screen.height - topOffset - footerOffset; // Height between header and footer
 
            window.open(
                iframeSrc,
                "thirdPartyPopup",
                "width=" + window.screen.width +
                ", height=" + adjustedHeight +
                ", top=" + topOffset + ", left=0, scrollbars=yes, resizable=yes, fullscreen=no, toolbar=no, location=no, status=no, titlebar=no, menubar=no, noopener=yes, noreferrer=yes"
            );
        } catch (error) {
            console.error(error);
        }
    };
 
    const commonCss = buildCssFromJson(getCommonStyles(feedbackData.content));
    const headerContent = getFeedbackSection(feedbackData.content, 'headerSection', 1);
    const bodyContentRaw = getFeedbackSection(feedbackData.content, 'bodySection', 2);
    const bodyContent = bodyContentRaw ? replaceBlobUrl(bodyContentRaw) : null;
    const footerContent = getFeedbackSection(feedbackData.content, 'footerSection', 3);
 
    return (
        <div className='feedback-main' style={{ minHeight: '600px', color: '#333', textAlign: 'center' }}>
            {/* 0th Section: Layout styles (structured JSON from DB, rendered as a
                trusted <style> text node — not dangerouslySetInnerHTML). */}
            {commonCss && <style>{commonCss}</style>}
 
            {/* 1st Section: Header section */}
            {headerContent && (
                <div
                    dangerouslySetInnerHTML={{
                        __html: sanitizeHtml(headerContent, "feedback-header")
                    }}
                />
            )}
 
            {/* 2nd Section: Body section */}
            {bodyContent && (
                <div
                    dangerouslySetInnerHTML={{
                        __html: sanitizeHtml(bodyContent, "feedback-body")
                    }}
                />
            )}
 
            {/* 3rd Section: Buttons */}
            <div>
                <div className="float-container">
                    <div className="float-child">
                        <div className='icon'></div>
                        <div className='content'>
                            <PrimaryButton
                                className="feedback-button"
                                onClick={() => handleButtonClick(feedbackData?.linkDetails[0].linkUrl, feedbackData?.linkDetails[0].linkText)}
                                tabIndex={0}
                                aria-label={`${feedbackData?.linkDetails[0].linkText || 'Give Product Feedback'} - Opens external feedback form in new window`}
                            >
                                <img
                                    className="feedback-icon"
                                    src={`${process.env.REACT_APP_BLOBSASSTART}Person Feedback.svg${blobToken}`}
                                    alt="Feedback Icon"
                                    width="20"
                                    height="20"
                                />
                                <span className="feedback-text">
                                    {feedbackData?.linkDetails[0].linkText || 'Give Feedback'}
                                </span>
                            </PrimaryButton>
                        </div>
                    </div>
                    <div className="float-child">
                        <div className='icon'></div>
                        <div className='content'>
                            <PrimaryButton
                                className="feedback-button"
                                onClick={() => handleButtonClick(feedbackData?.linkDetails[1].linkUrl, feedbackData?.linkDetails[1].linkText)}
                                tabIndex={0}
                                aria-label={`${feedbackData?.linkDetails[1].linkText || 'Give Product Feedback'} - Opens external feedback form in new window`}
                            >
                                <img
                                    className="feedback-icon"
                                    src={`${process.env.REACT_APP_BLOBSASSTART}Person Feedback.svg${blobToken}`}
                                    alt="Feedback Icon"
                                    width="20"
                                    height="20"
                                />
                                <span className="feedback-text">
                                    {feedbackData?.linkDetails[1].linkText || 'Give Product Feedback'}
                                </span>
                            </PrimaryButton>
                        </div>
                    </div>
                </div>
            </div>
 
            <div className="feedback-button-container">
                
            </div>
 
            {/* 4th Section: Footer Content */}
            {footerContent && (
                <div
                    dangerouslySetInnerHTML={{
                        __html: sanitizeHtml(footerContent, "feedback-footer")
                    }}
                />
            )}
        </div>
    );
};
 
export default FeedbackTabComponent;
 
// Generated by Copilot
// updated on: 2025-10-14 | 145 lines added/updated | Auto Generated | Created complete feedback tab component with API integration, dynamic content rendering, popup functionality, hover effects, loading and error states | Time Taken 25 min
// Generated by Copilot
// updated on: 2025-10-29 | 34 lines added/updated | [Auto Generated] | Added linkDetails array support, multi-button rendering section, enhanced handleButtonClick naming/log message | Time Taken 4 min
// Generated by Copilot
// updated on: 2026-07-21 | 5 lines added/updated | [Auto Generated] | US 1280933 AC-3 (#12996): sanitize feedback common/header/body/footer HTML via shared sanitizeHtml | Time Taken 4 min
// Generated by Copilot
// updated on: 2026-08-03 | 70 lines added/updated | [Auto Generated] | US 1288679: render DB-driven layout from structured commonStyles JSON via buildCssFromJson (<style> text node), deterministic section mapping; keeps #12996 sanitizer intact | Time Taken 15 min